<template>
  <a-modal
    v-if="visible"
    v-model:visible="visible"
    :title="title"
    width="70%"
    :bodyStyle="{ padding: '0', height: '70vh' }"
    :footer="null"
    @cancel="handleCancel"
  >
    <div class="multi-template-print-container">
      <!-- 左侧：模板列表 -->
      <div class="template-list-panel">
        <div class="panel-header">
          <FileTextOutlined style="margin-right: 8px;" />
          <span>打印模版</span>
        </div>
        <div class="template-list-content">
          <a-list
            :data-source="templates"
            size="small"
          >
            <template #renderItem="{ item }">
              <a-list-item
                :class="{ 'template-item-selected': selectedTemplateId === item.id }"
                class="template-item"
                @click="selectTemplate(item)"
              >
                <a-list-item-meta>
                  <template #title>
                    <span>{{ item.name || item.templateName }}</span>
                  </template>
                  <template #description>
                    <span class="template-desc">{{ item.remark || item.description || '暂无描述' }}</span>
                  </template>
                </a-list-item-meta>
              </a-list-item>
            </template>
          </a-list>
        </div>
      </div>

      <!-- 右侧：打印预览 -->
      <div class="template-preview-panel">
        <div class="panel-header">
          <FileTextOutlined style="margin-right: 8px;" />
          <span>打印预览</span>
        </div>
        <div class="preview-content">
          <div v-if="!printIframeUrl" class="preview-placeholder">
            <FileTextOutlined style="font-size: 64px; color: #d9d9d9; margin-bottom: 16px;" />
            <p style="color: #999;">请从左侧选择一个打印模板进行预览</p>
            <a-button
              v-if="selectedTemplateId"
              type="primary"
              @click="handleConfirmPrint"
              style="margin-top: 16px;"
            >
              <PrinterOutlined style="margin-right: 8px;" />
              确认打印
            </a-button>
          </div>
          <iframe
            v-else
            :key="iframeKey"
            :src="printIframeUrl"
            class="print-iframe"
            frameborder="0"
          ></iframe>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { FileTextOutlined, PrinterOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import emitter from '@/events/eventBus';
import SysConfigUtils from '@/utils/sysConfigUtils';
import { StorageUtils } from 'xmgoing-utils';

interface Template {
  id: string;
  name?: string;
  templateName?: string;
  remark?: string;
  description?: string;
  publishStatus?: number | string;
}

interface PrintConfig {
  printUrl: string;
  method: string;
  recordId: string;
  fileName: string;
}

const visible = ref(false);
const title = ref('多模板打印');
const templates = ref<Template[]>([]);
const selectedTemplateId = ref<string>('');
const selectedTemplate = ref<Template | null>(null);
const printConfig = ref<PrintConfig | null>(null);
const printIframeUrl = ref<string>('');
const iframeKey = ref<number>(0); // 添加 key 强制刷新 iframe
const onSelectCallback = ref<((templateId: string) => void) | null>(null);

// 选择模板
const selectTemplate = (template: Template) => {
  selectedTemplateId.value = template.id;
  selectedTemplate.value = template;

  // 生成打印预览 URL
  generatePrintPreviewUrl(template);
};

// 生成打印预览 URL（参考 config.ts 中的逻辑）
const generatePrintPreviewUrl = (template: Template) => {
  if (!printConfig.value) return;

  const paramsObj: any = {};
  paramsObj['recordId'] = printConfig.value.recordId;
  paramsObj['templateId'] = template.id;
  paramsObj['fileName'] = printConfig.value.fileName;
  paramsObj['sessionId'] = StorageUtils.getSessionItem("sessionId");
  paramsObj['url'] = printConfig.value.printUrl;
  paramsObj['projectI18n'] = StorageUtils.getItem('projectI18n');
  // 添加时间戳参数强制刷新
  paramsObj['_t'] = Date.now();

  // 构建 URL
  let printUrl = SysConfigUtils.xmPrintPage + `/xm-print-des/#/printByPdf`;

  // 添加参数
  const params = new URLSearchParams();
  Object.keys(paramsObj).forEach(key => {
    if (paramsObj[key] !== undefined && paramsObj[key] !== null) {
      params.append(key, paramsObj[key]);
    }
  });

  // 使用 window.openUrl 或直接构建 URL
  printUrl = printUrl + '?' + params.toString();

  // 更新 URL 并增加 key 强制 iframe 重新渲染
  printIframeUrl.value = printUrl;
  iframeKey.value++; // 改变 key 强制重新创建 iframe
};

const onPrintTemplateUpdated = (event: MessageEvent) => {
  if (event.data?.type !== 'xm-print-template-updated') return;
  if (!visible.value || !selectedTemplate.value) return;
  if (event.data.templateId && event.data.templateId !== selectedTemplateId.value) return;
  generatePrintPreviewUrl(selectedTemplate.value);
};

// 确认打印
const handleConfirmPrint = () => {
  if (!selectedTemplateId.value) {
    message.warning('请先选择一个打印模板！');
    return;
  }

  if (onSelectCallback.value) {
    onSelectCallback.value(selectedTemplateId.value);
  }

  visible.value = false;
  resetState();
};

// 取消按钮
const handleCancel = () => {
  visible.value = false;
  resetState();
};

// 重置状态
const resetState = () => {
  selectedTemplateId.value = '';
  selectedTemplate.value = null;
  printIframeUrl.value = '';
  iframeKey.value = 0; // 重置 iframe key
  templates.value = [];
  printConfig.value = null;
  onSelectCallback.value = null;
};

// 显示选择窗口
const showTemplateSelect = (data: {
  templates: Template[];
  printUrl: string;
  method: string;
  recordId: string;
  fileName: string;
  onSelectTemplate: (templateId: string) => void;
}) => {
  templates.value = data.templates;
  selectedTemplateId.value = '';
  selectedTemplate.value = null;
  printIframeUrl.value = '';
  printConfig.value = {
    printUrl: data.printUrl,
    method: data.method,
    recordId: data.recordId,
    fileName: data.fileName,
  };
  onSelectCallback.value = data.onSelectTemplate;
  visible.value = true;

  // 存在默认模板（publishStatus 为 1）时定位到默认模板，否则取第一个
  if (data.templates && data.templates.length > 0) {
    const defaultTemplate = data.templates.find((item) => Number(item.publishStatus) === 1);
    selectTemplate(defaultTemplate || data.templates[0]);
  }
};

// 监听事件总线
onMounted(() => {
  emitter.on('openMultiTemplatePrintDrawer', showTemplateSelect);
  window.addEventListener('message', onPrintTemplateUpdated);
});

onUnmounted(() => {
  emitter.off('openMultiTemplatePrintDrawer', showTemplateSelect);
  window.removeEventListener('message', onPrintTemplateUpdated);
});
</script>

<style lang="less" scoped>
.multi-template-print-container {
  display: flex;
  height: 70vh;
  border-top: 1px solid #f0f0f0;
}

// 左侧模板列表
.template-list-panel {
  width: 240px;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
  background-color: #fafafa;

  .panel-header {
    padding: 12px 16px;
    font-weight: 500;
    border-bottom: 1px solid #f0f0f0;
    background-color: #fff;
    display: flex;
    align-items: center;
    color: #4A4A4A;
    font-size: 14px;
  }

  .template-list-content {
    flex: 1;
    overflow-y: auto;
    padding: 8px 0;

    .template-item {
      padding: 8px 16px;
      cursor: pointer;
      transition: background-color 0.3s;
      border-bottom: 1px solid #f0f0f0;

      &:hover {
        background-color: #e6f7ff;
      }

      &.template-item-selected {
        background-color: #bae7ff;
      }

      .template-desc {
        font-size: 12px;
        color: #999;
      }
    }
  }
}

// 右侧预览
.template-preview-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #fff;

  .panel-header {
    padding: 12px 16px;
    font-weight: 500;
    border-bottom: 1px solid #f0f0f0;
    background-color: #fff;
    display: flex;
    align-items: center;
    color: #4A4A4A;
    font-size: 14px;
  }

  .preview-content {
    flex: 1;
    position: relative;

    .preview-placeholder {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #999;
    }

    .print-iframe {
      width: 100%;
      height: 100%;
      border: none;
    }
  }
}
</style>
